<script setup lang="ts">
import { useStore } from 'vuex';
import trackEvent, { THIRD_PARTY_PLACEMENT_POPUP_EVENT } from '@/modules/common/utils/tracker';

type Props = {
  id: string[];
  useCases: string[];
};
const props = defineProps<Props>();

const store = useStore();

const open = ref<boolean>(false);
let timer: ReturnType<typeof setTimeout>;
const shopifyDomain = computed(() => store.getters['app/shopifyDomain']);
const isShowThirdPartyPlacementPopup = computed(() => props.useCases.includes('click-through-page'));

const handleSubmit = () => {
  trackEvent({
    event: 'EDITOR_CLICK_TRY_NOW_THIRD_PARTY_PLACEMENT_POPUP',
    data: {
      event_id: THIRD_PARTY_PLACEMENT_POPUP_EVENT.EDITOR_CLICK_TRY_NOW_THIRD_PARTY_PLACEMENT_POPUP,
      store_domain: shopifyDomain.value,
      library_template_id: props.id,
      third_party_app_name: 'searchpie-v6',
      time: new Date().toISOString(),
    },
  });
  window.open('https://partners.secomapp.com/apps/20off/searchpie/GP_Preview', 'blank');
};
const handleClose = () => {
  trackEvent({
    event: 'EDITOR_HIDE_THIRD_PARTY_PLACEMENT_POPUP',
    data: {
      event_id: THIRD_PARTY_PLACEMENT_POPUP_EVENT.EDITOR_HIDE_THIRD_PARTY_PLACEMENT_POPUP,
      store_domain: shopifyDomain.value,
      library_template_id: props.id,
      third_party_app_name: 'searchpie-v6',
      time: new Date().toISOString(),
    },
  });
  open.value = false;
};

onMounted(() => {
  clearTimeout(timer);
  timer = setTimeout(() => {
    open.value = true;
    trackEvent({
      event: 'EDITOR_SHOW_THIRD_PARTY_PLACEMENT_POPUP',
      data: {
        event_id: THIRD_PARTY_PLACEMENT_POPUP_EVENT.EDITOR_SHOW_THIRD_PARTY_PLACEMENT_POPUP,
        store_domain: shopifyDomain.value,
        library_template_id: props.id,
        third_party_app_name: 'searchpie-v6',
        time: new Date().toISOString(),
      },
    });
  }, 5000);
});

onUnmounted(() => {
  open.value = false;
  clearTimeout(timer);
});
</script>

<template>
  <div
    v-if="open && isShowThirdPartyPlacementPopup"
    class="third-party-placement sticky bottom-[16px] left-1/2 flex h-[68px] w-[536px] -translate-x-1/2 items-center rounded-[16px] border-none bg-white px-2">
    <div class="h-[40px] w-[40px]">
      <img
        alt="third-party-placement"
        class="object-fit h-full w-full"
        src="../../../assets/images/third-party-placement.png" />
    </div>
    <div class="ml-[12px] flex w-[315px] flex-col">
      <div class="text-[14px] font-bold leading-[24px] text-[#212121]">Want webpages to mobile load faster?</div>
      <div class="text-[12px] leading-[20px] text-[#212121]">Let’s convert normal pages to AMP with SearchPie</div>
    </div>
    <button
      class="ml-[14px] flex h-[40px] cursor-pointer select-none items-center justify-center overflow-hidden whitespace-nowrap rounded-[8px] border border-[#f4f4f4] bg-[#f4f4f4] px-2 text-[12px] font-medium text-[#212121] outline-none transition-colors duration-200 hover:bg-[#e2e2e2] disabled:cursor-not-allowed"
      type="grey"
      :light-mode="false"
      @click="handleSubmit">
      Try now
    </button>
    <button tabindex="-1" class="group ml-1 h-[36px] w-[36px] p-0" @click="handleClose">
      <div
        class="flex h-full w-full flex-none items-center justify-center rounded-[8px] bg-white !text-[#676767] outline-none group-hover:bg-black/10">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M13.9697 15.0303C14.2626 15.3232 14.7374 15.3232 15.0303 15.0303C15.3232 14.7374 15.3232 14.2626 15.0303 13.9697L11.0607 10L15.0303 6.03033C15.3232 5.73744 15.3232 5.26256 15.0303 4.96967C14.7374 4.67678 14.2626 4.67678 13.9697 4.96967L10 8.93934L6.03033 4.96967C5.73744 4.67678 5.26256 4.67678 4.96967 4.96967C4.67678 5.26256 4.67678 5.73744 4.96967 6.03033L8.93934 10L4.96967 13.9697C4.67678 14.2626 4.67678 14.7374 4.96967 15.0303C5.26256 15.3232 5.73744 15.3232 6.03033 15.0303L10 11.0607L13.9697 15.0303Z"
            fill="#212121" />
        </svg>
      </div>
    </button>
  </div>
</template>

<style scoped lang="scss">
.third-party-placement {
  box-shadow: 0 4px 6px -2px rgba(26, 26, 26, 0.2), 0 1px 0 rgba(204, 204, 204, 0.5) inset,
    0 -1px 0 0 rgba(0, 0, 0, 0.17) inset, -1px 0 0 0 rgba(0, 0, 0, 0.13) inset, 1px 0 0 0 rgba(0, 0, 0, 0.13) inset;
}
</style>
